import React, { useState, useEffect } from "react";
import { User, Search, ChevronDown, ChevronUp, Trash2, MonitorSmartphone } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

const UsersDevicesPanel = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedUser, setExpandedUser] = useState(null);
  
  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    const fetchUsersDevices = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        
        // Llamada al nuevo endpoint para administradores con parámetros de paginación
        const response = await axios.get(`${API_URL}/api/v1/userDevices/admin/all-devices`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            page: currentPage,
            limit: itemsPerPage
          }
        });
        
        // Asumiendo que el backend devuelve { data: [...], pagination: { totalItems, totalPages } }
        // Si el backend aún no soporta paginación, adaptamos la respuesta actual
        if (response.data.pagination) {
          setUsers(response.data.data);
          setTotalItems(response.data.pagination.totalItems);
          setTotalPages(response.data.pagination.totalPages);
        } else {
          // Adaptación temporal si el backend no soporta paginación
          const allUsers = response.data;
          setTotalItems(allUsers.length);
          setTotalPages(Math.ceil(allUsers.length / itemsPerPage));
          
          // Simulamos paginación en el frontend
          const startIndex = (currentPage - 1) * itemsPerPage;
          const endIndex = startIndex + itemsPerPage;
          setUsers(allUsers.slice(startIndex, endIndex));
        }
      } catch (err) {
        console.error("Error al cargar datos de dispositivos:", err.response || err);
        setError(
          err.response?.data?.message || 
          `Error al cargar los datos (${err.message}). Por favor, verifica que estés conectado y tengas permisos de administrador.`
        );
      } finally {
        setLoading(false);
      }
    };

    fetchUsersDevices();
  }, [currentPage, itemsPerPage]);

  const toggleExpand = (userId) => {
    setExpandedUser(expandedUser === userId ? null : userId);
  };

  const handleDeleteDevice = async (userId, deviceId) => {
    if (!window.confirm("¿Estás seguro de que deseas eliminar este dispositivo? Esta acción no se puede deshacer.")) {
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/api/v1/userDevices/user/${userId}/device/${deviceId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      // Actualizar el estado local para reflejar la eliminación
      setUsers(prevUsers => 
        prevUsers.map(user => {
          if (user.id === userId) {
            return {
              ...user,
              devices: user.devices.filter(device => device.deviceId !== deviceId)
            };
          }
          return user;
        })
      );
    } catch (err) {
      console.error("Error al eliminar dispositivo:", err.response || err);
      setError("No se pudo eliminar el dispositivo. " + (err.response?.data?.error || err.message));
    }
  };

  // Funciones para la paginación
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  // Filtrar usuarios basados en el término de búsqueda
  const filteredUsers = searchTerm
    ? users.filter(
        (user) =>
          user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : users;

  // Contar el total de dispositivos
  const totalDevices = users.reduce((sum, user) => sum + (user.devices?.length || 0), 0);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">Error:</strong>
        <span className="block sm:inline"> {error}</span>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Barra de búsqueda */}
      <div className="mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search size={18} className="text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-4 py-2 border border-gray-600 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Buscar usuario por nombre o email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Contador de resultados */}
      <div className="mb-4 text-gray-400">
        Mostrando {filteredUsers.length} usuarios {searchTerm ? "filtrados" : `(página ${currentPage} de ${totalPages})`} | Total: {totalItems} usuarios con {totalDevices} dispositivos
      </div>

      {/* Lista de usuarios con sus dispositivos */}
      {filteredUsers.length > 0 ? (
        <div className="space-y-4">
          {filteredUsers.map((user) => (
            <div
              key={user.id}
              className="border border-gray-700 rounded-lg overflow-hidden bg-gray-800"
            >
              {/* Cabecera del usuario (siempre visible) */}
              <div
                className="p-4 flex flex-wrap md:flex-nowrap items-center justify-between cursor-pointer hover:bg-gray-750"
                onClick={() => toggleExpand(user.id)}
              >
                <div className="flex items-center space-x-4 mb-2 md:mb-0">
                  <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                    <User size={20} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{user.name}</h3>
                    <p className="text-gray-400">{user.email}</p>
                  </div>
                </div>
                
                <div className="flex flex-wrap items-center gap-3 md:gap-5">
                  <div className="text-center">
                    <div className="text-sm text-gray-400">Rol</div>
                    <div className="font-medium text-blue-400">{user.role}</div>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-sm text-gray-400">Dispositivos</div>
                    <div className="font-medium text-white">{user.devices?.length || 0}</div>
                  </div>
                  
                  <div className="ml-2">
                    {expandedUser === user.id ? (
                      <ChevronUp size={20} className="text-blue-400" />
                    ) : (
                      <ChevronDown size={20} className="text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Contenido expandible - Tabla de dispositivos */}
              {expandedUser === user.id && (
                <div className="border-t border-gray-700 p-4">
                  <div>
                    <h4 className="text-lg font-medium text-white mb-3 flex items-center">
                      <MonitorSmartphone className="mr-2" /> Dispositivos Conectados
                    </h4>
                    
                    {user.devices && user.devices.length > 0 ? (
                      <div className="bg-gray-900 rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-700">
                            <thead className="bg-gray-800">
                              <tr>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Nombre del Dispositivo</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Dirección IP</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Último Acceso</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Registrado</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Acciones</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700">
                              {user.devices.map((device) => (
                                <tr key={device.id} className="hover:bg-gray-750">
                                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-white">
                                    {device.deviceName || "Dispositivo sin nombre"}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                                    {device.ipAddress || "N/A"}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                                    {device.lastAccess ? new Date(device.lastAccess).toLocaleString() : "No disponible"}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                                    {device.createdAt ? new Date(device.createdAt).toLocaleString() : "No disponible"}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteDevice(user.id, device.deviceId);
                                      }}
                                      className="inline-flex items-center px-3 py-1 border border-red-800 text-xs rounded-md bg-red-900 text-red-200 hover:bg-red-800"
                                    >
                                      <Trash2 size={14} className="mr-1" /> Eliminar
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-400">Este usuario no tiene dispositivos conectados.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-gray-800 p-6 rounded-lg text-center">
          <p className="text-gray-400">No se encontraron usuarios que coincidan con la búsqueda.</p>
        </div>
      )}
      
      {/* Paginación */}
      {!loading && !error && users.length > 0 && !searchTerm && (
        <div className="px-4 py-3 bg-gray-800 border-t border-gray-700 sm:px-6 flex justify-center mt-4">
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <button
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-700 text-sm font-medium 
                  ${currentPage === 1 
                    ? 'bg-gray-800 text-gray-500 cursor-not-allowed' 
                    : 'bg-gray-900 text-gray-300 hover:bg-gray-700'}`}
              >
                <span className="sr-only">Anterior</span>
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </button>
              
              {/* Números de página */}
              {Array.from({ length: Math.min(5, totalPages) }).map((_, index) => {
                // Cálculo para mostrar las páginas correctas cuando hay muchas
                let pageNumber;
                
                if (totalPages <= 5) {
                  // Si hay 5 o menos páginas, mostrar todas
                  pageNumber = index + 1;
                } else {
                  // Si hay más de 5 páginas, mostrar de forma inteligente
                  if (currentPage <= 3) {
                    pageNumber = index + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNumber = totalPages - 4 + index;
                  } else {
                    pageNumber = currentPage - 2 + index;
                  }
                }
                
                return (
                  <button
                    key={pageNumber}
                    onClick={() => paginate(pageNumber)}
                    className={`relative inline-flex items-center px-4 py-2 border border-gray-700 text-sm font-medium
                      ${currentPage === pageNumber
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-900 text-gray-300 hover:bg-gray-700'}`}
                  >
                    {pageNumber}
                  </button>
                );
              })}
              
              <button
                onClick={goToNextPage}
                disabled={currentPage >= totalPages}
                className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-700 text-sm font-medium 
                  ${currentPage === totalPages 
                    ? 'bg-gray-800 text-gray-500 cursor-not-allowed' 
                    : 'bg-gray-900 text-gray-300 hover:bg-gray-700'}`}
              >
                <span className="sr-only">Siguiente</span>
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </button>
            </nav>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersDevicesPanel;
